import { IStorageService } from './StorageService';

/**
 * Implementation based on https://developer.mozilla.org/en-US/docs/Web/API/Document/cookie/Simple_document.cookie_framework
 */
export class CookiesStorage implements IStorageService {
  private static bSecure: boolean = false;
  private static sPath = '';
  private static sDomain = '';
  private static vEnd = 3600 * 2; // 2 Hours expiry time
  private static MAX_COOKIE_SIZE = 4093; // Bytes

  public static escapeKey(sKey: string): string {
    return encodeURIComponent(sKey).replace(/[\-\.\+\*]/g, '\\$&');
  }

  public getItem(sKey: string) {
    if (!sKey) {
      return null;
    }
    const key = CookiesStorage.escapeKey(sKey);
    const expression = new RegExp(
      '(?:(?:^|.*;)\\s*' + key + '\\s*\\=\\s*([^;]*).*$)|^.*$',
    );
    return decodeURIComponent(
      document.cookie.replace(expression, '$1') || null,
    );
  }

  public setItem(sKey: string, sValue: string) {
    if (!sKey || /^(?:expires|max\-age|path|domain|secure)$/i.test(sKey)) {
      return false;
    }
    let sExpires = '';
    if (CookiesStorage.vEnd) {
      switch (CookiesStorage.vEnd.constructor) {
        case Number:
          sExpires =
            CookiesStorage.vEnd === Infinity
              ? '; expires=Fri, 31 Dec 9999 23:59:59 GMT'
              : '; max-age=' + CookiesStorage.vEnd;
          /*
          Note: Despite officially defined in RFC 6265, the use of `max-age` is not compatible with any
          version of Internet Explorer, Edge and some mobile browsers. Therefore passing a number to
          the end parameter might not work as expected. A possible solution might be to convert the the
          relative time to an absolute time. For instance, replacing the previous line with:
          */
          /*
          sExpires = vEnd === Infinity ? "; expires=Fri, 31 Dec 9999 23:59:59 GMT" : "; expires=" + (new Date(vEnd * 1e3 + Date.now())).toUTCString();
          */
          break;
        case String:
          sExpires = '; expires=' + CookiesStorage.vEnd;
          break;
      }
    }

    const cookie = [
      encodeURIComponent(sKey),
      '=',
      encodeURIComponent(sValue),
      sExpires,
      CookiesStorage.sDomain ? '; domain=' + CookiesStorage.sDomain : '',
      CookiesStorage.sPath ? '; path=' + CookiesStorage.sPath : '',
      CookiesStorage.bSecure ? '; secure' : '',
    ].join('');

    if (cookie.length > CookiesStorage.MAX_COOKIE_SIZE) {
      throw new Error('Unable to store more than 4KiB in a single cookie');
    }
    document.cookie = cookie;
    return true;
  }

  public removeItem(sKey: string) {
    if (!this.hasItem(sKey)) {
      return false;
    }
    document.cookie = [
      encodeURIComponent(sKey),
      '=; expires=Thu, 01 Jan 1970 00:00:00 GMT',
      CookiesStorage.sDomain ? '; domain=' + CookiesStorage.sDomain : '',
      CookiesStorage.sPath ? '; path=' + CookiesStorage.sPath : '',
    ].join('');

    return true;
  }

  public hasItem(sKey: string) {
    if (!sKey || /^(?:expires|max\-age|path|domain|secure)$/i.test(sKey)) {
      return false;
    }

    const value = encodeURIComponent(sKey).replace(/[\-\.\+\*]/g, '\\$&');
    const expression = new RegExp('(?:^|;\\s*)' + value + '\\s*\\=');

    return expression.test(document.cookie);
  }

  public keys() {
    const replaceExpression =
      /((?:^|\s*;)[^\=]+)(?=;|$)|^\s*|\s*(?:\=[^;]*)?(?:\1|$)/g; // eslint-disable-line no-useless-backreference
    const aKeys = document.cookie
      .replace(replaceExpression, '')
      .split(/\s*(?:\=[^;]*)?;\s*/);
    for (let nLen = aKeys.length, nIdx = 0; nIdx < nLen; nIdx++) {
      aKeys[nIdx] = decodeURIComponent(aKeys[nIdx]);
    }
    return aKeys;
  }

  public clear() {
    this.keys().forEach((k) => this.removeItem(k));
  }

  public get length() {
    return this.keys().length;
  }

  public key(index: number): string | null {
    const keyName = this.keys()[index] || null;

    if (!keyName) {
      return null;
    }

    return this.getItem(keyName);
  }
}
